import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { TbColorPicker, TbWorld, TbExternalLink, TbX, TbArrowRight } from 'react-icons/tb';
import { api } from '../../lib/api';

const MAX_COLORS = 12;
const PaletteContext = createContext({ colors: [], add: () => {}, remove: () => {}, website: '', setWebsite: () => {}, referenceUrl: null, setReferenceUrl: () => {} });
const normalizeColor = value => {
  if (typeof value !== 'string') return null;
  const color = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(color)) return color;
  if (/^#[0-9a-f]{3}$/.test(color)) return '#' + color.slice(1).split('').map(c => c + c).join('');
  return null;
};

export function WebsiteColorsProvider({ value, onChange, children }) {
  const [local, setLocal] = useState({});
  const saved = (onChange ? value : local) || {};
  const colors = Array.isArray(saved.colors) ? [...new Set(saved.colors.map(normalizeColor).filter(Boolean))].slice(0, MAX_COLORS) : [];
  const latest = useRef(saved);
  latest.current = saved;
  const change = update => {
    const next = update(latest.current);
    latest.current = next;
    if (onChange) onChange(next); else setLocal(next);
  };
  const [referenceUrl, setReferenceUrl] = useState(() => websiteAddress(saved.sourceUrl || ''));
  const add = value => {
    const color = normalizeColor(value);
    if (color) change(previous => ({ ...previous, colors: [color, ...(previous.colors || []).filter(c => c !== color)].slice(0, MAX_COLORS) }));
  };
  return <PaletteContext.Provider value={{ colors, add, website: saved.website || '',
    setWebsite: website => change(previous => ({ ...previous, website })), referenceUrl, setReferenceUrl,
    replace: (colors, sourceUrl) => change(previous => ({ ...previous, colors, sourceUrl })),
    remove: color => change(previous => ({ ...previous, colors: (previous.colors || []).filter(c => normalizeColor(c) !== color) })) }}>
    {children}
  </PaletteContext.Provider>;
}

export function ScreenColorPicker({ onPick, label = 'Pick from screen', disabled = false }) {
  const palette = useContext(PaletteContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(null);
  const supported = typeof window.EyeDropper === 'function' && window.isSecureContext !== false;
  useEffect(() => () => { pending.current?.abort(); pending.current = null; }, []);
  useEffect(() => {
    if (disabled) { pending.current?.abort(); pending.current = null; setBusy(false); }
  }, [disabled]);
  const pick = async () => {
    if (!supported || disabled || pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    setError(''); setBusy(true);
    try {
      // Must happen directly in the click handler to retain browser user activation.
      const result = await new window.EyeDropper().open({ signal: controller.signal });
      if (controller.signal.aborted || pending.current !== controller) return;
      const color = normalizeColor(result.sRGBHex);
      if (!color) throw new Error('Invalid color');
      onPick(color);
      palette.add(color);
    } catch (err) {
      if (pending.current === controller && !controller.signal.aborted && err.name !== 'AbortError') {
        setError('Screen picking could not start. Try again, or use the color picker or hex field.');
      }
    } finally {
      if (pending.current === controller) { pending.current = null; setBusy(false); }
    }
  };
  return <>
    <button type="button" className="v2-screen-color" onClick={pick} disabled={!supported || disabled || busy}
      aria-label={label} title={supported ? 'Load your website in the website preview, then pick a visible pixel. Escape cancels.' : 'Screen picking is unavailable in this browser. Use the color picker or hex field.'}>
      <TbColorPicker size={15} aria-hidden="true" /> {busy ? 'Picking...' : 'Pick from screen'}
    </button>
    {error && <span className="v2-color-error" role="alert">{error}</span>}
  </>;
}

export function PaletteSwatches({ onChoose, label = 'this color', value, disabled = false }) {
  const { colors } = useContext(PaletteContext);
  if (!colors.length) return null;
  return <div className="v2-colorfield-swatches" role="group" aria-label={`Website palette for ${label}`}>
    {colors.map(color => <button type="button" key={color} className={`v2-colorfield-sw${normalizeColor(value) === color ? ' on' : ''}`}
      disabled={disabled} aria-label={`Use ${color} for ${label}`} title={color} style={{ background: color }} onClick={() => onChoose(color)} />)}
  </div>;
}

export function ColorField({ value, onChange, swatches, fallback, label, allowClear = true, disabled = false }) {
  const palette = useContext(PaletteContext);
  const currentColor = normalizeColor(value || fallback);
  return <fieldset className="v2-colorfield" disabled={disabled} aria-label={label ? `${label} controls` : undefined}>
    {swatches && <div className="v2-colorfield-swatches">{swatches.map(color => <button key={color} type="button" aria-label={color}
      className={`v2-colorfield-sw${value === color ? ' on' : ''}`} style={{ background: color }} onClick={() => onChange(color)} />)}</div>}
    <input type="color" className="v2-colorfield-picker" aria-label={label || 'Pick a colour'}
      value={currentColor || '#94a3b8'} onChange={event => onChange(event.target.value)} />
    <input className="v2-input v2-colorfield-hex" aria-label={label ? `${label} hex` : undefined} value={value}
      placeholder={fallback || 'Theme default'} onChange={event => onChange(event.target.value)} />
    <ScreenColorPicker disabled={disabled} onPick={onChange} label={`Pick ${label || 'color'} from screen`} />
    <button type="button" className="v2-colorfield-clear" disabled={!currentColor} onClick={() => palette.add(currentColor)}>Save color</button>
    {allowClear && value && <button type="button" className="v2-colorfield-clear" onClick={() => onChange('')}>Clear</button>}
    <PaletteSwatches disabled={disabled} onChoose={onChange} value={value} label={label || 'this color'} />
  </fieldset>;
}

export function WebsitePalette() {
  const palette = useContext(PaletteContext);
  const [draft, setDraft] = useState('');
  const save = () => { if (normalizeColor(draft)) { palette.add(draft); setDraft(''); } };
  return <div className="v2-website-palette">
    <p className="v2-hint">Load your website to detect its main colors. You can also pick or add colors, then reuse them across this widget.</p>
    <div className="v2-palette-count"><span>Saved with this widget when you publish</span><strong>{palette.colors.length} / {MAX_COLORS}</strong></div>
    <div className="v2-colorfield">
      <ScreenColorPicker onPick={() => {}} label="Pick a website palette color from screen" />
      <input className="v2-input v2-colorfield-hex" aria-label="Website palette hex" placeholder="#123456" value={draft}
        onChange={event => setDraft(event.target.value)} onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); save(); } }} />
      <button type="button" className="v2-screen-color" disabled={!normalizeColor(draft)} onClick={save}>Add color</button>
    </div>
    {typeof window.EyeDropper !== 'function' || window.isSecureContext === false
      ? <p className="v2-hint">Screen picking is unavailable here. Use a color picker or paste a hex color instead.</p> : null}
    <div className="v2-colorlist">{palette.colors.map(color => <span className="v2-chip" key={color}>
      <span className="v2-colordot" style={{ background: color }} />{color}
      <button type="button" className="v2-chip-x" aria-label={`Remove ${color} from website palette`} onClick={() => palette.remove(color)}>&times;</button>
    </span>)}</div>
  </div>;
}


function websiteAddress(value) {
  try {
    const text = value.trim();
    const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`);
    return ['https:', 'http:'].includes(url.protocol) && url.hostname && !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function WebsiteReferencePreview() {
  const palette = useContext(PaletteContext);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [detecting, setDetecting] = useState(false);
  const [detectionNote, setDetectionNote] = useState('');
  const request = useRef(0);
  useEffect(() => () => { request.current += 1; }, []);
  const url = websiteAddress(palette.website);
  const showWebsite = async () => {
    if (!url) return;
    const current = ++request.current;
    setFailed(false);
    setReload(previous => previous + 1);
    palette.setReferenceUrl(url);
    palette.replace([], url);
    setDetecting(true); setDetectionNote('');
    try {
      const result = await api.post('/s2/api/widgets/website-colors', { url });
      if (request.current !== current) return;
      const colors = [...new Set((result.colors || []).map(normalizeColor).filter(Boolean))].slice(0, MAX_COLORS);
      palette.replace(colors, url);
      setDetectionNote(colors.length ? 'Main website colors detected. Remove any you do not need.' : 'No colors detected. Pick from the preview or add a hex color.');
    } catch {
      if (request.current === current) setDetectionNote('Could not detect website colors. Pick from the preview or add a hex color.');
    } finally {
      if (request.current === current) setDetecting(false);
    }
  };
  return <section className="v2-website-reference" aria-label="Website reference preview">
    <header className="v2-studio-panel-head">
      <div className="v2-studio-panel-title"><span className="v2-studio-panel-icon"><TbWorld size={18} /></span><div><h3>Website reference</h3><p>Your brand, right beside your design</p></div></div>
      <span className="v2-reference-badge">Color match</span>
    </header>
    <div className="v2-reference-addressbar">
      <label className="v2-reference-url"><TbWorld size={16} aria-hidden="true" /><input className="v2-input v2-website-address" aria-label="Website URL for color picking" inputMode="url" placeholder="yourwebsite.com"
        value={palette.website} onChange={event => palette.setWebsite(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); showWebsite(); } }} /></label>
      <button type="button" className="v2-screen-color v2-reference-go" disabled={!url} onClick={showWebsite}>Show website<TbArrowRight size={15} aria-hidden="true" /></button>
    </div>
    {palette.referenceUrl ? <>
      <div className="v2-reference-actions">
        <ScreenColorPicker onPick={() => {}} label="Pick a color from website preview" />
        <a className="v2-reference-external" href={palette.referenceUrl} target="_blank" rel="noopener noreferrer"><TbExternalLink size={15} aria-hidden="true" />Open separately</a>
        <button type="button" className="v2-reference-close" aria-label="Close website" title="Close website" onClick={() => { request.current += 1; setDetecting(false); palette.setReferenceUrl(null); }}><TbX size={16} /></button>
      </div>
      {failed && <p className="v2-color-error" role="alert">The website could not load here. Use Open separately.</p>}
      <div className="v2-website-reference-viewport">
        <iframe key={`${palette.referenceUrl}:${reload}`} src={palette.referenceUrl} title="Website color reference"
          sandbox="allow-scripts allow-forms allow-same-origin" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
      </div>
      <div className="v2-reference-footer">
        <details className="v2-reference-help"><summary>Website not loading?</summary><p>Some websites block embedded previews. Use Open separately if the page stays blank.</p></details>
        <span>Scroll to browse · drag to resize</span>
      </div>
      <div className="v2-reference-saved">
        <p className="v2-hint" role="status">{detecting ? 'Detecting website colors…' : detectionNote || 'Website palette · saved with this widget'}</p>
        <div className="v2-colorlist">{palette.colors.map(color => <span className="v2-chip" key={color}>
          <span className="v2-colordot" style={{ background: color }} />{color}
          <button type="button" className="v2-chip-x" aria-label={`Remove ${color} from detected colors`} onClick={() => palette.remove(color)}>&times;</button>
        </span>)}</div>
      </div>
    </> : <div className="v2-reference-empty">
      <div className="v2-reference-illustration" aria-hidden="true"><div className="v2-reference-browser"><i /><i /><i /><span /></div><div className="v2-reference-wireframe"><div /><span /><span /><b /><section><i /><i /><i /></section></div><div className="v2-reference-color-fan"><i /><i /><i /><i /></div></div>
      <h4>Bring your website into view</h4>
      <p>Enter your URL above. We will detect the main website colors so you can make your widget match.</p>
      <span className="v2-reference-empty-note"><TbColorPicker size={14} />Detect colors. Keep your favorites.</span>
    </div>}
  </section>;
}
